import React, { useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import debounce from 'lodash.debounce';
import { useTranslation } from 'react-i18next';
import ToolbarItem from './ToolbarItem';
import { DEFAULT_TOOL, useTools, ToolWindowProps, ToolProps } from '../../tools/Tools';
import { ToolNames } from '../../tools/ToolNames';
import useAppState from '../../AppStore';
import { useTracking } from '../../utils/tracking/TrackingContext';

const Toolbar = () => {
  const { t } = useTranslation('agisviewer');
  const { Tracking } = useTracking();
  const tools = useTools();
  const defaultTools = Object.entries(tools).slice(0, 2);
  const extendedTools = Object.entries(tools).slice(2);
  const extendedToolsCount = extendedTools.length;

  const [skip, setSkip] = useState(0);
  const [visibleItemCount, setVisibleItemCount] = useState(extendedToolsCount);
  const [debouncedTotalHeight, setDebouncedTotalHeight] = useState(2000);

  const toolBarItemsRef = useRef<HTMLUListElement>(null);

  const viewCollapse = useAppState((state) => state.viewCollapse);
  const toggleViewCollapse = useAppState((state) => state.toggleViewCollapse);
  const isOpen = useAppState((state) => state.toolbar.isOpen);
  const toggleToolbar = useAppState((state) => state.toggleToolbar);
  const activeTool = useAppState((state) => state.activeTool);
  const setActiveTool = useAppState((state) => state.setActiveTool);
  const activeView = useAppState((state) => state.activeView);
  const enabledIdentify = useAppState((state) => state.enabledIdentify);

  const debounceResize = useCallback(debounce(setDebouncedTotalHeight, 100), []);

  useEffect(() => {
    const handleWindowResize = () => {
      if (!toolBarItemsRef.current) return;
      debounceResize(toolBarItemsRef.current.clientHeight);
    };
    window.addEventListener('resize', handleWindowResize);

    return () => {
      window.removeEventListener('resize', handleWindowResize);
      debounceResize.cancel();
    };
  }, []);

  useEffect(() => {
    if (!toolBarItemsRef.current) return;
    const elementHeight = toolBarItemsRef.current.lastElementChild?.clientHeight ?? 1;
    const totalHeight = toolBarItemsRef.current.clientHeight;
    const itemCount = Math.floor(totalHeight / elementHeight) - 6;

    if (itemCount >= extendedToolsCount - 1) setSkip(0);
    setVisibleItemCount(itemCount);
  }, [debouncedTotalHeight, toolBarItemsRef]);

  const setTake = () => {
    let result = visibleItemCount;
    if (skip === 0) {
      result += 1;
      return result === 1 ? result + 1 : result;
    }

    return result < 1 ? 1 : result;
  };

  const take = setTake();
  const nextSkip = skip + take;

  const scrollDown = () => setSkip(nextSkip);

  const scrollUp = () => {
    const prevSkip = skip - take;
    setSkip((prevSkip === 1 && visibleItemCount > 1) || prevSkip < 0 ? 0 : prevSkip);
  };

  const setToolView = (toolName: ToolNames, view?: ToolWindowProps[]) => {
    const mainView = view?.find((v) => v.isMainView)?.key || '';

    if (toolName !== activeTool || mainView !== activeView) {
      Tracking.Tool.openTool(toolName, 'Toolbar');
      setActiveTool(toolName, mainView);
    } else {
      Tracking.Tool.closeTool(toolName, 'Toolbar');
      setActiveTool(DEFAULT_TOOL);
    }
  };

  const handleOnClick = (toolName: string, tool: ToolProps) => {
    tool.onClick?.();
    if (tool.views) setToolView(toolName as ToolNames, tool.views);
  };

  const isLastPage = nextSkip >= extendedToolsCount;
  const lastItemIndex = isLastPage ? extendedToolsCount : nextSkip;
  const firstItemIndex =
    extendedToolsCount > take && isLastPage ? lastItemIndex - take - 1 : skip;

  const isIdentifyEnabled = (toolName: string): boolean => {
    return (
      toolName === ToolNames.identify &&
      enabledIdentify &&
      activeTool !== ToolNames.draw &&
      activeTool !== ToolNames.print
    );
  };

  const isToolActive = (toolName: string): boolean => {
    return (
      (toolName === activeTool && toolName !== ToolNames.identify) ||
      isIdentifyEnabled(toolName)
    );
  };

  const handleCollapseOnClick = () => {
    Tracking.UI.toogleFullscreen(
      viewCollapse ? 'out_fullscreen' : 'enter_fullscreen',
      'Toolbar',
    );
    toggleViewCollapse();
  };

  const handleToolbarToggle = () => {
    Tracking.Toolbar.toggle(isOpen ? 'minimize' : 'expand');
    toggleToolbar();
  };

  return (
    <menu
      className={classNames('toolbar', {
        'toolbar--show': isOpen,
        'toolbar--collapsed': viewCollapse,
      })}
    >
      <ul
        ref={toolBarItemsRef}
        className={classNames('toolbar__items', { 'toolbar__items--expand': isOpen })}
      >
        {defaultTools.map(([toolName, tool]) => (
          <ToolbarItem
            key={toolName}
            label={t(tool.label)}
            title={t(tool.tooltip ?? tool.label)}
            additionalClassNames={classNames({
              toolbar__separator: tool.addSeparator === true && visibleItemCount > 0,
            })}
            icon={tool.icon}
            isActive={toolName === activeTool}
            disabled={tool.disabled}
            onClick={() => setToolView(toolName as ToolNames, tool.views)}
            badge={tool.badge}
          />
        ))}

        {skip > 0 && (
          <ToolbarItem icon="chevron-up" onClick={scrollUp} title="nach oben" />
        )}

        {extendedTools.slice(firstItemIndex, lastItemIndex).map(([toolName, tool]) => (
          <ToolbarItem
            key={toolName}
            label={t(tool.label)}
            title={t(tool.tooltip ?? tool.label)}
            additionalClassNames={classNames({
              toolbar__separator:
                tool.addSeparator === true && take >= extendedToolsCount,
            })}
            icon={tool.icon}
            isActive={isToolActive(toolName)}
            disabled={tool.disabled}
            badge={tool.badge}
            onClick={() => handleOnClick(toolName, tool)}
          />
        ))}

        {nextSkip < extendedToolsCount && (
          <ToolbarItem icon="chevron-down" onClick={scrollDown} title="nach unten" />
        )}

        <ToolbarItem
          additionalClassNames="toolbar__item--push-to-bottom"
          icon={isOpen ? 'backward' : 'forward'}
          title={isOpen ? t('toolbar_collapse') : t('toolbar_expand')}
          onClick={handleToolbarToggle}
        />
        <ToolbarItem
          additionalClassNames={classNames({ 'toolbar__item-collapsed': viewCollapse })}
          icon={viewCollapse ? 'view-contract' : 'view-expand'}
          title={viewCollapse ? t('view_collapse') : t('view_expand')}
          onClick={handleCollapseOnClick}
        />
      </ul>
    </menu>
  );
};

export default Toolbar;
